/* One shared, in-memory history for every room and detail in the Bagyard. */
(() => {
'use strict';
if (typeof modal === 'undefined' || !window.history || window.BagyardNavigation) return;
const adapters = window.BagyardNavigationStates || (window.BagyardNavigationStates = {});
const session = 'bagyard-' + Date.now().toString(36), entries = new Map();
let cursor = 0, serial = 0, restoring = false, restored = null, gesture = null, pending = false, moving = false;
let restoreTask = Promise.resolve(), historyHashUrl = null;
const clone = value => value == null ? value : JSON.parse(JSON.stringify(value));
const nativeShow = show, nativeGo = goScene;
const cleanUrl = () => location.pathname + location.search + location.hash;
const marker = id => ({...(history.state || {}), bagyardNavigation:{session,id}});
const elements = root => [root,...root.querySelectorAll('*')];
// cloneNode does not copy live form values, scroll positions or property handlers.
// Keep these in memory only; never put a note or an application into a URL/storage.
function copyTree(source) {
  const copy = source.cloneNode(true), from = elements(source), to = elements(copy);
  from.forEach((node,i) => {
    const other = to[i];
    if (/^(INPUT|TEXTAREA|SELECT)$/.test(node.tagName) && node.type !== 'file') other.value = node.value;
    if ('checked' in node) other.checked = node.checked;
    other._bagyardScroll = node._bagyardScroll || [node.scrollLeft || 0,node.scrollTop || 0];
    for (const name of ['onclick','onchange','oninput','onkeydown']) if (typeof node[name] === 'function') other[name] = node[name];
  });
  return copy;
}
function snapshot() {
  const states = {};
  for (const [key,adapter] of Object.entries(adapters)) states[key] = clone(adapter.read());
  return {scene:currentScene,pan,viewMode,open:modal.open,tree:modal.open?copyTree(content):null,
    classes:modal.className,style:modal.getAttribute('style') || '',scroll:modal.scrollTop || 0,
    pageScroll:[window.scrollX || 0,window.scrollY || 0],states,url:cleanUrl(),
    focus:modal.open?elements(content).indexOf(document.activeElement):-1};
}
function buttons() {
  const button = document.getElementById('close');
  button.innerHTML = '<span aria-hidden="true">←</span> <span>戻る</span>';
  button.setAttribute('aria-label','一つ前に戻る');
  const room = document.getElementById('bagyard-back');
  if (room) { room.hidden = currentScene === 'entrance'; room.disabled = !entries.get(cursor)?.previous && entries.get(cursor)?.previous !== 0; }
}
function updateCurrent() {
  const entry = entries.get(cursor);
  if (entry && !restoring && !moving) entries.set(cursor,{...snapshot(),previous:entry.previous});
}
function commit(before) {
  if (restoring) return;
  const after = snapshot();
  if (before && before.scene === after.scene && before.open === after.open && (!after.open || (before.tree.innerHTML === after.tree.innerHTML && JSON.stringify(before.states) === JSON.stringify(after.states)))) { updateCurrent(); buttons(); return; }
  const previous = cursor, prior = entries.get(previous);
  if (before) entries.set(previous,{...before,previous:prior?.previous ?? null});
  // Kitchen routes may have used replaceState while rendering the next screen.
  history.replaceState(marker(previous),'',entries.get(previous)?.url || after.url);
  cursor = ++serial;
  entries.set(cursor,{...after,previous});
  history.pushState(marker(cursor),'',after.url);
  buttons();
}
function schedule(before) {
  if (restoring || pending || moving) return;
  pending = true;
  queueMicrotask(() => { pending = false; if (!moving) commit(before); });
}
function stopMedia() {
  content.querySelectorAll('video,audio').forEach(media => { try { media.pause(); } catch {} });
  // Embedded videos must stop too; a restored screen never starts playback by itself.
  content.querySelectorAll('iframe').forEach(frame => frame.remove());
  window.BagyardCrew?.unmount?.();
  const sheet = document.getElementById('vs-related-dialog');
  if (sheet) { sheet.close(); sheet.remove(); }
}
async function restore(id) {
  const target = entries.get(id);
  if (!target) return;
  restoring = true; restored = target; gesture = null;
  stopMedia();
  try {
    if (!target.open && modal.open) {
      await new Promise(resolve => { modal.addEventListener('close',resolve,{once:true}); modal.close(); });
    }
    if (currentScene !== target.scene) await nativeGo(target.scene);
    pan = target.pan; viewMode = target.viewMode; layoutScene();
    for (const [key,value] of Object.entries(target.states)) adapters[key]?.restore(clone(value));
    if (target.open) {
      if (!modal.open) nativeShow('');
      const tree = copyTree(target.tree);
      content.replaceChildren(...tree.childNodes);
      modal.className = target.classes;
      modal.setAttribute('style',target.style);
      document.body.style.overflow = 'hidden';
      elements(content).forEach(node => { const position = node._bagyardScroll; if (position) { node.scrollLeft=position[0];node.scrollTop=position[1]; } });
      for (const adapter of Object.values(adapters)) adapter.mount?.();
      // Do not restore autoplay from an embedded player or resume a video unexpectedly.
      content.querySelectorAll('iframe').forEach(frame => { if (frame.src?.includes('autoplay=1')) frame.src=frame.src.replace('autoplay=1','autoplay=0'); });
      modal.scrollTop = target.scroll;
      const focus = elements(content)[target.focus];
      (focus?.focus ? focus : document.getElementById('close')).focus({preventScroll:true});
    } else document.body.style.overflow = '';
    cursor = id;
    history.replaceState(marker(id),'',target.url);
    window.scrollTo(...target.pageScroll);
    buttons();
  } finally { restoring = false; restored = null; }
}
function back() {
  if (restoring || moving || isMoving) return;
  if (entries.get(cursor)?.previous != null) { updateCurrent(); history.back(); }
  else if (modal.open) { modal.close(); queueMicrotask(updateCurrent); }
}
show = function(html) {
  const before = gesture?.before || snapshot();
  nativeShow(html);
  buttons();
  // Only deliberate navigation creates an entry. Network refreshes replace it.
  if (gesture && !gesture.edit) { gesture.handled=true; schedule(before); }
  else if (!gesture && !before.open && modal.open) schedule(before);
  else queueMicrotask(updateCurrent);
};
goScene = async function(key) {
  if (restoring) return nativeGo(key);
  if (!(key in sceneDefs) || isMoving || moving || key === currentScene) return {scene:currentScene};
  const before = gesture?.before || snapshot();
  if (gesture) gesture.handled=true;
  moving = true;
  try { const result = await nativeGo(key); moving = false; if (!result?.error) commit(before); return result; }
  finally { moving = false; buttons(); }
};
const backSelector = '#close,#bagyard-back,[data-nav-back],[data-return-room],[data-bench-close],[data-wear-back],[data-k-gear-back],[data-k-gear-return],[data-v-close-related]';
const innerNavigation = '[data-v-mode],[data-v-all-models],[data-v-open-related],[data-v-pick-model],[data-v-related-model],[data-v-same-model],[data-v-browse],[data-v-find],[data-film-lens],[data-film-clip],[data-k-tab]';
window.addEventListener('click',e => {
  const button = e.target.closest?.('button,a');
  const dialog = e.target.closest?.('dialog');
  const rect = dialog === e.target ? dialog.getBoundingClientRect() : null;
  const backdrop = rect && (e.clientX<rect.left || e.clientX>rect.right || e.clientY<rect.top || e.clientY>rect.bottom);
  if (e.target.closest?.(backSelector) || backdrop) { e.preventDefault();e.stopImmediatePropagation();back();return; }
  if (!button || restoring || moving) return;
  updateCurrent();
  const current = {before:snapshot(),inner:button.matches(innerNavigation),edit:button.matches('[data-free-delete],[data-free-cancel],[data-v-action="save"],[data-v-remove],[data-k-save],[data-crew-remove]')};
  gesture = current;
  // Browsers run microtasks between event listeners. Keep the gesture alive
  // through target/bubble handlers, including handlers registered by old modules.
  setTimeout(() => {
    if (current.inner && !current.handled && !moving) commit(current.before);
    if (gesture===current) gesture=null;
  },0);
},true);
window.addEventListener('submit',e => {
  if (restoring) return;
  const current = {before:snapshot(),edit:e.target.id==='by-free-note-form'};gesture=current;
  setTimeout(() => {if(gesture===current)gesture=null;},0);
},true);
window.addEventListener('cancel',e => { if(e.target===modal || modal.contains(e.target)){e.preventDefault();e.stopImmediatePropagation();back();} },true);
window.addEventListener('popstate',e => {
  const target = e.state?.bagyardNavigation;
  if (target?.session !== session || !entries.has(target.id)) return;
  // The browser has changed URL already; preserve the old screen under its old URL.
  if (!restoring) {
    const old = entries.get(cursor), fresh = snapshot();
    entries.set(cursor,{...fresh,url:old.url,previous:old.previous});
  }
  historyHashUrl=entries.get(target.id).url;
  restoreTask=restoreTask.then(()=>restore(target.id));
});
// Existing direct-link listeners must not reopen a screen after a history restore.
window.addEventListener('hashchange',e => {if(historyHashUrl===cleanUrl()){e.stopImmediatePropagation();historyHashUrl=null;}},true);
const roomButton=document.createElement('button');
roomButton.id='bagyard-back';roomButton.className='glass-button by-history-back';roomButton.type='button';
roomButton.innerHTML='<span aria-hidden="true">←</span><span>戻る</span>';roomButton.setAttribute('aria-label','一つ前に戻る');
document.querySelector('.header-tools')?.prepend(roomButton);
window.BagyardNavigation={back,update:updateCurrent,restoredState:key=>restored?.states[key]};
async function start() {
  // A direct link belongs to its own room, so closing it never exposes the shutter.
  if (modal.open && currentScene==='entrance') {
    const room=content.querySelector('.vs-shell,.by-lens-films')?'polarized':content.querySelector('.k-shell')?'kitchen':content.querySelector('.bc-shell,.op-shell,.custom-guide')?'bags':'workshop';
    moving=true;await nativeGo(room);moving=false;
  }
  const first=snapshot();
  entries.set(0,{...first,open:false,tree:null,previous:null});
  history.replaceState(marker(0),'',first.url);
  if(first.open){cursor=++serial;entries.set(cursor,{...first,previous:0});history.pushState(marker(cursor),'',first.url);}
  buttons();
}
void start();
})();
